"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcFlooring, type FlooringResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import CitationSection from "@/components/CitationSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const INSTALLATION_OPTIONS = [
  { value: "straight", label: "Straight / Simple Layout (+5%)" },
  { value: "staggered", label: "Staggered / Standard (+10%)" },
  { value: "diagonal", label: "Diagonal / Complex (+15%)" },
  { value: "herringbone", label: "Herringbone / Chevron (+20%)" },
];

const FLOOR_TYPE_OPTIONS = [
  { value: "hardwood", label: "Hardwood / Engineered Wood" },
  { value: "lvp", label: "Vinyl Plank (LVP)" },
  { value: "tile", label: "Ceramic / Porcelain Tile" },
  { value: "carpet", label: "Carpet" },
  { value: "laminate", label: "Laminate" },
];

export default function CalculatorClient() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [installType, setInstallType] = useState<"straight" | "staggered" | "diagonal" | "herringbone">("staggered");
  const [floorType, setFloorType] = useState("hardwood");
  const [coveragePerBox, setCoveragePerBox] = useState("");
  const [result, setResult] = useState<FlooringResult | null>(null);

  function handleCalculate() {
    const res = calcFlooring({
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      installationType: installType,
      coveragePerBox: parseFloat(coveragePerBox) || undefined,
    });
    setResult(res);
  }

  const isCarpet = floorType === "carpet";

  return (
    <CalculatorShell
      name="Flooring Calculator"
      description="Estimate how much flooring you need with the right waste factor for your installation pattern."
      icon="flooring"
      category="diy"
      accentColor="#d97706"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Estimates account for standard waste factor. Diagonal laying patterns or complex room shapes require 15% waste — verify total area before purchasing."
      proTip="Keep 1 extra box of planks in storage for future repairs, as flooring styles are frequently discontinued."
      inputsPanel={
        <div className="space-y-4">
          <SelectField id="floor-type" label="Flooring Type" value={floorType} onChange={(e) => { setFloorType(e.target.value); setResult(null); }} options={FLOOR_TYPE_OPTIONS} />
          <div className="grid grid-cols-2 gap-3">
            <InputField id="floor-length" label="Room Length" unit="ft" type="number" min="0" step="0.5" placeholder="15" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
            <InputField id="floor-width" label="Room Width" unit="ft" type="number" min="0" step="0.5" placeholder="12" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
          </div>
          <SelectField id="install-type" label="Installation Pattern" value={installType} onChange={(e) => { setInstallType(e.target.value as typeof installType); setResult(null); }} options={INSTALLATION_OPTIONS} hint="Diagonal and herringbone patterns require more cuts = more waste" />
          <InputField
            id="coverage-per-box"
            label="Coverage per Box (optional)"
            unit="sq ft"
            type="number"
            min="0"
            step="0.5"
            placeholder="20"
            value={coveragePerBox}
            onChange={(e) => { setCoveragePerBox(e.target.value); setResult(null); }}
            hint="Check your flooring box label for this number"
          />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard
              label={`Total Area to Purchase (${result.wastePercent}% waste)`}
              value={result.totalAreaSqFt}
              unit="sq ft"
              highlight
              size="lg"
              sub={`Net area: ${result.netAreaSqFt} sq ft`}
            />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Square Meters" value={result.totalAreaSqM} unit="m²" accent />
              {isCarpet
                ? <ResultCard label="Square Yards" value={result.totalAreaSqYd} unit="yd²" accent sub="Carpet is sold in sq yd" />
                : <ResultCard label="Net Sq Ft" value={result.netAreaSqFt} unit="sq ft" />
              }
            </div>
            {result.boxesNeeded !== undefined && (
              <ResultCard label="Boxes to Purchase" value={result.boxesNeeded} unit="boxes" highlight sub={`Based on ${coveragePerBox} sq ft per box`} />
            )}
            {/* Waste breakdown */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Waste Factor Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "Net area", value: `${result.netAreaSqFt} sq ft` },
                  { label: `Waste factor (${result.wastePercent}%)`, value: `+${(result.totalAreaSqFt - result.netAreaSqFt).toFixed(1)} sq ft` },
                  { label: "Total to purchase", value: `${result.totalAreaSqFt} sq ft` },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)]"
                  >
                    <span className="text-[12.5px] font-sans font-semibold text-[var(--ink-soft)]">{row.label}</span>
                    <span className="font-mono text-[13px] text-[var(--ink)] font-bold">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Flooring & Tile Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Choosing the right <Link href="/articles/vinyl-vs-laminate-vs-hardwood-flooring/" className="text-[var(--accent)] hover:underline font-semibold">flooring types</Link> involves assessing both moisture resistance and traffic durability. If you&apos;re selecting Luxury Vinyl Plank (LVP), ensure a wear layer of at least <strong>{CALCULATOR_SPECS.flooring.buyingGuide.wearLayerResidentialMin.value} mils</strong> for normal residential use, or <strong>{CALCULATOR_SPECS.flooring.buyingGuide.wearLayerCommercialMin.value} mils</strong> for high-traffic or pet areas. For solid hardwood, check the Janka hardness rating to gauge dent resistance. Before starting, let solid hardwood or laminate floor planks acclimate inside the climate-controlled installation room for at least <strong>{CALCULATOR_SPECS.flooring.buyingGuide.acclimationTimeHardwood.value} hours</strong> to prevent subsequent warping, shrinking, or buckling.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Flooring is ordered by the carton, roll, or pallet, so the final purchase should round up to whole packages and include matching lot numbers when possible. Keep at least one unopened box for future repairs; discontinued colors and embossing patterns are hard to match after a few years.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Match the material to the room. Carpet hides subfloor flaws but dislikes moisture, laminate is sensitive to standing water, LVP tolerates damp areas better, tile needs a stiff substrate, and solid hardwood should be used carefully over slabs or in humidity swings.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/flooring_material_guide.webp" alt="Samples of vinyl planks, laminate, and engineered wood flooring shown side-by-side" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/flooring_site_prep.webp" alt="Plywood subfloor moisture testing using relative humidity meter" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Flooring & Tile Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Subfloor condition dictates the lifespan of your floor. The joist deflection must not exceed the building code&apos;s <strong>{CALCULATOR_SPECS.flooring.sitePrep.deflectionLimit.value}</strong> standard, especially under tile or engineered wood. Subfloors must be structurally sound, flat, and dry. Review our <Link href="/articles/flooring-installation-cost-per-square-foot/" className="text-[var(--accent)] hover:underline font-semibold">flooring installation cost guide</Link> to budget professional labor or DIY prep. For concrete slabs, relative humidity (RH) must measure below <strong>{CALCULATOR_SPECS.flooring.sitePrep.concreteMoistureRHMax.value}%</strong> before installing LVP or laminate. Lastly, always leave a 1/4-inch expansion gap around the room perimeter; failing to do so will cause the flooring to buckle or separate as it responds to seasonal humidity changes.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Flatness is different from level. A floor can slope and still be installable if it is flat within the product tolerance, while small humps or dips can break click-lock joints and telegraph through resilient flooring. Mark high spots, fill low spots, and remove loose fasteners before underlayment goes down.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Plan transitions before installation begins. Doorways, stairs, fireplaces, cabinets, sliding doors, and long hallways may require reducers, stair noses, T-molding, undercut jambs, or expansion breaks that should be ordered with the flooring.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.flooring.industryStandards.expansionSpanMax.standardCode!}
        sourceName={CALCULATOR_SPECS.flooring.industryStandards.expansionSpanMax.sourceName}
        sourceUrl={CALCULATOR_SPECS.flooring.industryStandards.expansionSpanMax.sourceUrl}
        paragraphs={[
          <p key="p1">
            Wood and wood-based flooring products are hygroscopic, meaning they dynamically absorb and release moisture from the air, causing physical expansion and contraction across the grain. Under the <b>{CALCULATOR_SPECS.flooring.industryStandards.expansionSpanMax.standardCode}</b> standard outlined by the NWFA, floating luxury vinyl plank (LVP), laminate, and solid hardwood floors must not exceed a continuous run of <b>{CALCULATOR_SPECS.flooring.industryStandards.expansionSpanMax.value} feet</b> in length or width without an expansion joint. Run sizes exceeding this threshold accumulate enough physical movement to pinch the flooring against walls, leading to buckling or joint failure.
          </p>,
          <p key="p2">
            To accommodate seasonal shifts, installers must leave a perimeter expansion gap—typically 1/4 to 1/2 inch—around walls and all fixed vertical obstructions. When the span exceeds the maximum allowed length, T-molding transition strips must be introduced, particularly at doorways and narrow passageways. Additionally, homeowners should maintain indoor relative humidity between 30% and 50% year-round to minimize floor movement and protect planks from permanent cup warping or separation gaps.
          </p>,
          <p key="p3">
            A useful flooring estimate includes flooring, underlayment, vapor retarder where required, transitions, stair pieces, adhesive or fasteners, floor prep, trim removal, base shoe, disposal, and furniture moving. Waste percentage does not cover missing accessory pieces.
          </p>,
          <p key="p4">
            Moisture testing should be completed before sensitive products are delivered. Concrete slabs, crawlspaces, recent water damage, and below-grade rooms can all require a different product or moisture-control detail than a dry second-floor bedroom.
          </p>
        ]}
        keyRequirements={[
          "Maximum continuous run of 30 feet in length/width without T-molding expansion joints.",
          "Perimeter expansion gaps of 1/4 to 1/2 inch must be left around all walls.",
          "Doorway and narrow pass-through transitions require physical T-molding separators.",
          "Indoor relative humidity must be maintained between 30% and 50% year-round.",
          "Order whole cartons and keep matching attic stock for future repairs.",
          "Verify subfloor flatness, dryness, and deflection before installation.",
          "Include transitions, stair pieces, underlayment, and trim work in the budget."
        ]}
      />

      <FaqSection calculatorId="flooring" />
      <RelatedCalculators calculatorId="flooring" />
    </CalculatorShell>
  );
}
